import axios from 'axios';


// Variables to hold the token retrieval and logout functions
let getAccessTokenSilentlyFunc = null;
let logoutFunc = null; // Added logout function holder

// Function to set the token retrieval function from outside
export const setAuth0TokenRetriever = (func) => {
  getAccessTokenSilentlyFunc = func;
};

// Function to set the logout function from outside
export const setAuth0Logout = (func) => {
  logoutFunc = func;
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://oysterapi.expressanalytics.net', // Fallback if env var is not set
  // Remove the default Content-Type header
  // headers: {
  //   'Content-Type': 'application/json',
  // },
});

// Request Interceptor to add JWT token
apiClient.interceptors.request.use(
  async (config) => {
    console.log('Request Interceptor: Processing request for', config.url);
    // Check if the token retriever function has been set
    if (getAccessTokenSilentlyFunc) {
      console.log('Request Interceptor: getAccessTokenSilentlyFunc is available.');
      try {
        console.log('Request Interceptor: Attempting to get access token...');
        const token = await getAccessTokenSilentlyFunc({
          authorizationParams: {
            audience: process.env.REACT_APP_API_BASE_URL || 'https://oysterapi.expressanalytics.net',
          },
          // Request a JWT instead of JWE for direct API usage
          detailedResponse: true,
          cacheMode: 'cache-first'
        });

        if (token) {
          console.log('Request Interceptor: Token received, adding Authorization header.');
          // If we get a detailed response with id_token, use it; otherwise fall back to the standard token
          const authToken = token.id_token || token;
          config.headers.Authorization = `Bearer ${authToken}`;

          // Debug token type
          const tokenParts = authToken.split('.');
          if (tokenParts.length === 3) {
            console.log('Request Interceptor: Using JWT token');
          } else if (tokenParts.length === 5) {
            console.log('Request Interceptor: Using JWE token');
          } else {
            console.warn('Request Interceptor: Unknown token format');
          }
        } else {
          console.warn('Request Interceptor: getAccessTokenSilentlyFunc returned null or undefined token.');
          // Potentially logout here if a missing token is critical
          // if (logoutFunc) {
          //   logoutFunc({ logoutParams: { returnTo: window.location.origin } });
          // }
        }
      } catch (error) {
        console.error('Request Interceptor: Error getting access token:', error);
        // If the error means login is required, trigger logout
        if (error.error === 'login_required' || error.error === 'consent_required') {
          console.warn('Interceptor: Login required. Triggering logout.');
          if (logoutFunc) {
            logoutFunc({ logoutParams: { returnTo: window.location.origin } });
          } else {
            console.error('Logout function not available in api.js');
          }
          return Promise.reject(new Error('Login required'));
        }
        // For other errors, log them but potentially allow the request to proceed without Authorization
        // This might result in a 401/403 from the backend, which the response interceptor can handle.
        console.warn('Request Interceptor: Proceeding without Authorization header due to token fetch error.');
      }
    } else {
      console.warn('Request Interceptor: getAccessTokenSilently function not set. Auth headers will be missing.');
    }

    console.log('Request Interceptor: Final Headers:', config.headers);
    return config;
  },
  (error) => {
    console.error('Request Interceptor: Error before sending request:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor for handling errors, especially 401
apiClient.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    return response;
  },
  (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    console.error('API Error:', error.response?.status, error.response?.data);

    // Check if it's a 401 Unauthorized error
    if (error.response?.status === 401) {
      console.warn('Received 401 Unauthorized from API. Potential token issue. Triggering logout.');
      // Trigger logout if a 401 is received
      if (logoutFunc) {
        logoutFunc({ logoutParams: { returnTo: window.location.origin } });
      } else {
        console.error('Logout function not available in api.js to handle 401.');
      }
      // Optionally, prevent further processing of this specific error by not rejecting it
      // return; // Or return a specific object indicating logout was triggered
    }

    // Reject the promise for other errors or if logout wasn't triggered
    return Promise.reject(error);
  }
);

// --- API Functions ---

// Fetch list of available models
export const getModels = async (logout) => {

  try {

    const response = await apiClient.get('/api/v1/models/list');
    return response.data;
  } catch (error) {
    console.error('Error fetching models123:', error);
    // Avoid throwing if logout was triggered by interceptor
    logout({ logoutParams: { returnTo: window.location.origin } });
    if (error.message !== 'Login required') {
      //throw error;
    }

  }
};

// Get health status for any model type
export const getModelHealth = async (modelType) => {
  try {
    const response = await apiClient.get(`/api/v1/${modelType}/health`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${modelType} health:`, error);
    if (error.message !== 'Login required') {
      throw error;
    }
    // Return a default structure with error status
    return {
      status: 'error',
      services: {
        model: {
          status: 'unknown'
        }
      }
    };
  }
};

// Fetch details for a specific model (Endpoint TBD)
export const getModelDetails = async (modelId) => {
  console.warn('getModelDetails function not fully implemented - Endpoint TBD');
  // Example of handling potential interceptor rejection
  try {
    // Placeholder logic
    return Promise.resolve({ id: modelId, name: `Model ${modelId}`, description: 'Details TBD' });
  } catch (error) {
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Train a specific model
export const trainModel = async (modelId, data) => {
  try {
    const response = await apiClient.post(`/api/v1/${modelId}/train`, data);
    return response.data;
  } catch (error) {
    console.error(`Error training model ${modelId}:`, error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Test a specific model
export const testModel = async (modelId, data) => {
  try {
    const response = await apiClient.post(`/api/v1/${modelId}/test`, data);
    return response.data;
  } catch (error) {
    console.error(`Error testing model ${modelId}:`, error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Add other model-specific functions as needed

// --- Lookalike Model Specific Functions ---

// Check Lookalike service health
export const checkLookalikeHealth = async () => {
  try {
    const response = await apiClient.get('/api/v1/lookalike/health');
    return response.data;
  } catch (error) {
    console.error('Error checking lookalike health:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Upload data for Lookalike model (Training or Testing)
export const uploadLookalikeData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/lookalike/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading lookalike data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const uploadVOCAData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/vocaapi/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading vocaapi data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const uploadAnalyticalData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/data-transfer/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error  uploadAnalyticalData data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const uploadRFMData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/rfm/upload-data', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading RFM data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const uploadDataQuality = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/data-quality/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading cltv data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const uploadCLTVData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/cltv/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading cltv data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};


export const uploadCIDRData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/deduplication/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading deduplication data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const uploadMMMData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/mmm-preprocessor/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading MMM data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};


export const uploadMTAData = async (formData, onUploadProgress) => {
  try {
    const response = await apiClient.post('/api/v1/mta-attribution/mta-attribution/upload', formData, {
      onUploadProgress: onUploadProgress
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading RFM data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Validate Lookalike data
export const validateLookalikeData = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/lookalike/validate', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating lookalike data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const useSampleDataApi = async (modelname, params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/' + modelname + '/use-sample-data', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating  data:', modelname, error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const validateVOCAProcess = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/vocaapi/process', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating vocaapi data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Validate-segment Lookalike data
export const validateRFMData = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/rfm/segment', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating lookalike data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const validateCLTVData = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/cltv/process', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating lookalike data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const validateDataQuality = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/data-quality/process', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validateDataQuality data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const validateCIDRData = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/deduplication/deduplicate', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating deduplication data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Validate-segment Lookalike data
export const validateMMMData = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/mmm-preprocessor/preprocess', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating mmm data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const validateMTAData = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/mta-attribution/mta-attribution/analyze', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating lookalike data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Train Lookalike model
export const trainLookalikeModel = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/lookalike/train', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error training lookalike model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Train Lookalike model
export const rfmTaskStatusapi = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/rfm/task-status/${taskId}`);
    return response.data;


  } catch (error) {
    console.error('Error training lookalike model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const cltvTaskStatusapi = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/cltv/task-status/${taskId}`);
    return response.data;


  } catch (error) {
    console.error('Error cltvTaskStatusapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const dqTaskStatusapi = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/data-quality/task/${taskId}/status`);
    return response.data;


  } catch (error) {
    console.error('Error dqTaskStatusapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const cidrTaskStatusapi = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/deduplication/status/${taskId}`);
    return response.data;


  } catch (error) {
    console.error('Error cidrTaskStatusapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const mmmTaskStatusapi = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/mmm-preprocessor/task-status/${taskId}`);
    return response.data;


  } catch (error) {
    console.error('Error mmmTaskStatusapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};


export const mtaTaskStatusapi = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/mta-attribution/mta-attribution/task/${taskId}/status`);
    return response.data;


  } catch (error) {
    console.error('Error mtaTaskStatusapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const mtaResultapi = async (emailId, modelId, taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/mta-attribution/mta-attribution/results/${emailId}/${modelId}/${taskId}`);
    return response.data;


  } catch (error) {
    console.error('Error mtaResultapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Get Lookalike training lift metrics
export const getLookalikeTrainingMetrics = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/lookalike/training-lift-metrics', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting lookalike training metrics:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Predict using Lookalike model
export const predictLookalikeModel = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/lookalike/predict', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error predicting with lookalike model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const predictVOCAModel = async (params) => {
  try {
    const urlEncodedData = new URLSearchParams(params).toString();
    const response = await apiClient.post('/api/v1/vocaapi/results', urlEncodedData, {
      headers: {
        // Ensure correct header is set for this specific request type
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error predicting with vocaapi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const cltvResultModelApi = async (taskId, output_type) => {
  try {
    const encodedTaskId = encodeURIComponent(taskId);

    const response = await apiClient.get(`/api/v1/cltv/results/${encodedTaskId}?output_type=${output_type}`);
    return response.data;
  } catch (error) {
    console.error('Error predicting with cltv model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const dataQualityResultModelApi = async (taskId) => {
  try {
    const encodedTaskId = encodeURIComponent(taskId);

    const response = await apiClient.get(`/api/v1/data-quality/results/${encodedTaskId}`);
    return response.data;
  } catch (error) {
    console.error('Error predicting with cltv model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const dataQualityDownloadApi = async (taskId) => {
  try {
    const encodedTaskId = encodeURIComponent(taskId);

    const response = await apiClient.get(`/api/v1/data-quality/results/${encodedTaskId}/download`,
      {
        responseType: "blob", // treat as binary file
      }
    );
    return response;
  } catch (error) {
    console.error('Error dataQualityDownloadApi model:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Get status of a Lookalike task
export const getLookalikeTaskStatus = async (taskId) => {
  try {
    // Task ID needs to be URL encoded if it contains special characters
    const encodedTaskId = encodeURIComponent(taskId);
    // Corrected endpoint: use 'tasks' (plural) and remove '/status'
    const response = await apiClient.get(`/api/v1/lookalike/tasks/${encodedTaskId}`);
    return response.data;
  } catch (error) {
    console.error(`Error getting status for task ${taskId}:`, error);
    // Avoid throwing if logout was triggered by interceptor
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export const getVOCATaskStatus = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/vocaapi/task-status/${encodeURIComponent(taskId)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting VOCA task status:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Check VOCA health
export const checkVOCAHealth = async () => {
  try {
    const response = await apiClient.get('/api/v1/vocaapi/health');
    return response.data;
  } catch (error) {
    console.error('Error checking VOCA health:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
    return {
      overall_status: 'error',
      services: {
        api: { status: 'unknown' }
      }
    };
  }
};

// Get RFM Task Status
export const getRFMTaskStatus = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/rfm/task-status/${encodeURIComponent(taskId)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting RFM task status:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Get RFM scatter chart data
export const getRFMScatterChartData = async (taskId) => {
  try {
    const response = await apiClient.get(`/api/v1/rfm/customers/${encodeURIComponent(taskId)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting RFM scatter chart data:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// ==================== VOCA ANALYTICS FUNCTIONS ====================

// Get VOCA Enhanced Results
export const getVOCAResults = async (taskId, includeAggregated = true) => {
  try {
    const params = includeAggregated ? '?include_aggregated=true' : '';
    const response = await apiClient.get(`/api/v1/vocaapi/results/${encodeURIComponent(taskId)}${params}`);
    return response.data;
  } catch (error) {
    console.error('Error getting VOCA results:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Start VOCA Topic Insights Analytics
export const startVOCATopicInsights = async (taskId, limit = 5, sentimentFilter = 'all') => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      sentiment_filter: sentimentFilter
    });
    const response = await apiClient.post(`/api/v1/vocaapi/analytics/${encodeURIComponent(taskId)}/topic-insights?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error starting VOCA topic insights:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Start VOCA Operational Dashboard Analytics
export const startVOCAOperationalDashboard = async (taskId) => {
  try {
    const response = await apiClient.post(`/api/v1/vocaapi/analytics/${encodeURIComponent(taskId)}/operational-dashboard`);
    return response.data;
  } catch (error) {
    console.error('Error starting VOCA operational dashboard:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Start VOCA Executive Dashboard Analytics
export const startVOCAExecutiveDashboard = async (taskId) => {
  try {
    const response = await apiClient.post(`/api/v1/vocaapi/analytics/${encodeURIComponent(taskId)}/executive-dashboard`);
    return response.data;
  } catch (error) {
    console.error('Error starting VOCA executive dashboard:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Start VOCA Recommendations Analytics
export const startVOCARecommendations = async (taskId, priorityLevel = 'all', category = 'all', quickWinsOnly = false, enhanced = true) => {
  try {
    const params = new URLSearchParams({
      priority_level: priorityLevel,
      category: category,
      quick_wins_only: quickWinsOnly.toString(),
      enhanced: enhanced.toString()
    });
    const response = await apiClient.post(`/api/v1/vocaapi/analytics/${encodeURIComponent(taskId)}/recommendations?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error starting VOCA recommendations:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Get VOCA Analytics Results
export const getVOCAAnalyticsResults = async (analyticsTaskId) => {
  try {
    const response = await apiClient.get(`/api/v1/vocaapi/analytics/results/${encodeURIComponent(analyticsTaskId)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting VOCA analytics results:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

// Check VOCA Analytics Task Status (wrapper for polling)
export const getVOCAAnalyticsTaskStatus = async (analyticsTaskId) => {
  try {
    const response = await apiClient.get(`/api/v1/vocaapi/analytics/results/${encodeURIComponent(analyticsTaskId)}`);

    // Check if response contains progress information indicating task is still running
    if (response.data?.detail?.message && response.data.detail.message.includes('in progress')) {
      return {
        status: 'in_progress',
        progress: response.data.detail.progress,
        stage: response.data.detail.stage,
        stage_description: response.data.detail.stage_description
      };
    }

    // If no progress info, assume completed with results
    return {
      status: 'completed',
      results: response.data
    };
  } catch (error) {
    console.error('Error checking VOCA analytics task status:', error);
    if (error.message !== 'Login required') {
      throw error;
    }
  }
};

export default apiClient;
